import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { MutationStateFns } from '@/api/utils/mutation';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY } from './useGetGroupNegotiations';

export type ApiRequest_Create_Group_Negotiation = {
  title: string;
  description?: string;
  end_date?: string;
  max_member_count?: number;
  metadata?: Record<string, unknown>;
};

export type ApiResponse_Create_Group_Negotiation = {
  id: number;
  community_id: number;
  initiator_id: number;
  title: string;
  description?: string;
  status: string;
  created_at: string;
  updated_at: string;
  closed_at?: string;
  end_date?: string;
  metadata?: Record<string, unknown>;
};

const METHOD = 'POST';
const ENDPOINT = 'communities/{communityId}/group-negotiations';

export const useCreateGroupNegotiation = (
  communityId: number,
  stateFns?: MutationStateFns<ApiResponse_Create_Group_Negotiation>
) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ApiRequest_Create_Group_Negotiation) =>
      handleApiCall<ApiResponse_Create_Group_Negotiation, ApiRequest_Create_Group_Negotiation>(
        session,
        METHOD,
        getResolvedEndpoint(ENDPOINT, { communityId: communityId.toString() }),
        data
      ),
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY({ communityId }),
      });
      enqueueSnackbar('Save together group created successfully!', { variant: 'success' });
      stateFns?.onSuccess?.(data);
    },
    onError: error => {
      stateFns?.onError?.(error as AxiosError);
    },
  });
};
